import { useCallback, useState } from 'react';
import { replaceSelection, toggleSelection, rangeSelection, selectAll } from '../selectionModel';

export interface ClickModifiers {
  /** Ctrl (Win/Linux) or Cmd (macOS) — toggle one item. */
  ctrlOrMeta: boolean;
  /** Shift — select the range from the anchor. */
  shift: boolean;
}

/**
 * Selection state, keyed on **entry ids** (`entryIdentity.ts`) — STD-029 R-2.
 *
 * Every parameter below is an `entryId(file)`, never a `file.key`: callers
 * resolve the entry to its id once, at the event boundary. Passing a display
 * path here is the bug this hook was re-keyed to end, and it fails silently —
 * the set simply never matches a card.
 */
export interface FileSelection {
  selectedIds: ReadonlySet<string>;
  selectionAnchor: string | null;
  isSelected: (id: string) => boolean;
  /** Apply a selection-intent click, honouring Ctrl/Cmd (toggle) and Shift (range). */
  selectOnClick: (id: string, modifiers: ClickModifiers, orderedIds: readonly string[]) => void;
  /** Replace the selection with exactly this entry (e.g. right-click outside the selection). */
  replaceWith: (id: string) => void;
  /** Set the selection to an explicit id set (marquee live-update; keep-failed-selected). */
  setSelected: (ids: ReadonlySet<string>) => void;
  selectAllIds: (orderedIds: readonly string[]) => void;
  clear: () => void;
}

/**
 * React-state wrapper over the pure `selectionModel` transitions. All selection
 * math lives in `selectionModel.ts` (unit-tested); this only holds state.
 */
export function useFileSelection(): FileSelection {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [selectionAnchor, setSelectionAnchor] = useState<string | null>(null);

  const replaceWith = useCallback((id: string) => {
    setSelectedIds(replaceSelection(id));
    setSelectionAnchor(id);
  }, []);

  const selectOnClick = useCallback(
    (id: string, modifiers: ClickModifiers, orderedIds: readonly string[]) => {
      if (modifiers.shift) {
        // Range from the anchor; anchor stays put so the range can be re-dragged.
        setSelectedIds((prev) => {
          void prev;
          return rangeSelection(orderedIds, selectionAnchor, id);
        });
      } else if (modifiers.ctrlOrMeta) {
        setSelectedIds((prev) => toggleSelection(prev, id));
        setSelectionAnchor(id);
      } else {
        setSelectedIds(replaceSelection(id));
        setSelectionAnchor(id);
      }
    },
    [selectionAnchor]
  );

  const selectAllIds = useCallback((orderedIds: readonly string[]) => {
    setSelectedIds(selectAll(orderedIds));
  }, []);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
    setSelectionAnchor(null);
  }, []);

  const setSelected = useCallback((ids: ReadonlySet<string>) => {
    setSelectedIds(new Set(ids));
  }, []);

  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  return {
    selectedIds,
    selectionAnchor,
    isSelected,
    selectOnClick,
    replaceWith,
    setSelected,
    selectAllIds,
    clear,
  };
}
